// This enhanced service worker registration utility supports additional
// functionalities such as update notifications, caching URLs, background sync,
// and message handling with the service worker.

// Determine if we are running on localhost
const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

// Use the enhanced service worker file for additional features.
const SW_URL = `${process.env.PUBLIC_URL}/enhanced-service-worker.js`;

/**
 * Register the enhanced service worker.
 * Handles registration, update detection, and messaging.
 */
export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin.
      return;
    }

    window.addEventListener('load', () => {
      if (isLocalhost) {
        // On localhost, verify that the service worker exists and is valid.
        checkValidServiceWorker(SW_URL, config);
        navigator.serviceWorker.ready.then(() => {
          console.log(
            '[ServiceWorker] This web app is being served cache-first by a service worker. ' +
            'To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Register service worker normally on non-localhost.
        registerValidSW(SW_URL, config);
      }
    });
  }
}

/**
 * Register a valid service worker and set up update and message handling.
 */
function registerValidSW(swUrl, config) {
  console.log('[ServiceWorker] Attempting to register service worker:', swUrl);
  
  navigator.serviceWorker
    .register(swUrl, { scope: '/' })
    .then(registration => {
      console.log('[ServiceWorker] Registration successful:', registration);
      
      // Store the registration globally for later use.
      window.swRegistration = registration;
      
      // Set up the update found handler.
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;
        
        console.log('[ServiceWorker] Installing new service worker...');
        installingWorker.onstatechange = () => {
          console.log('[ServiceWorker] Service worker state changed to:', installingWorker.state);
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content is available.
              console.log('[ServiceWorker] New version is available');
              
              // Dispatch a custom event to notify the app.
              const updateEvent = new CustomEvent('serviceWorkerUpdate', {
                detail: { registration }
              });
              window.dispatchEvent(updateEvent);
              
              // Execute the onUpdate callback if provided.
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Content is cached for offline use.
              console.log('[ServiceWorker] Content cached for offline use');
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };

      // Set up message handling for the service worker.
      navigator.serviceWorker.addEventListener('message', handleMessage);
    })
    .catch(error => {
      console.error('[ServiceWorker] Error during registration:', error);
    });
}

/**
 * Check if a valid service worker exists. If not, unregister and reload.
 */
function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then(response => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Unregister and reload the page.
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed with registration.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[ServiceWorker] No internet connection found. App is running in offline mode.');
    });
}

/**
 * Manually check for service worker updates.
 * @returns {Promise<boolean>} Resolves to true if an update is pending.
 */
export function checkForUpdates() {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.getRegistration().then(registration => {
      if (registration) {
        console.log('[ServiceWorker] Checking for service worker updates');
        return registration.update().then(() => {
          // If a waiting service worker exists, return true.
          return !!registration.waiting;
        });
      }
      return false;
    });
  }
  return Promise.resolve(false);
}

/**
 * Manually apply a pending update.
 * @returns {boolean} True if an update was applied, false otherwise.
 */
export function applyUpdate() {
  if (window.swRegistration && window.swRegistration.waiting) {
    console.log('[ServiceWorker] Applying pending service worker update');
    window.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // Listen for the controller change to reload the page.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[ServiceWorker] New service worker activated, reloading page');
      window.location.reload();
    });
    return true;
  } else {
    console.log('[ServiceWorker] No pending service worker update to apply');
    return false;
  }
}

/**
 * Handle messages received from the service worker.
 * This function can be extended to handle different message types.
 */
function handleMessage(event) {
  console.log('[ServiceWorker] Message received:', event.data);
  switch (event.data.type) {
    case 'CACHE_COMPLETE':
      console.log('[ServiceWorker] Cache operation completed');
      break;
    case 'BACKGROUND_SYNC_STARTED':
      console.log('[ServiceWorker] Background sync started');
      break;
    default:
      break;
  }
}

/**
 * Add a listener for update available events.
 * @param {Function} listener Callback that receives the registration.
 */
export function onUpdateAvailable(listener) {
  window.addEventListener('serviceWorkerUpdate', (event) => {
    try {
      listener(event.detail.registration);
    } catch (error) {
      console.error('[ServiceWorker] Error in update listener:', error);
    }
  });
}

/**
 * Request the service worker to cache specific URLs.
 * @param {Array<string>} urls URLs to cache.
 */
export function cacheUrls(urls) {
  if (window.swRegistration && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_URLS',
      urls
    });
  } else {
    console.log('[ServiceWorker] No active service worker to receive message');
  }
}

/**
 * Trigger a background sync operation.
 * @returns {Promise<boolean>} Resolves to true if sync was successfully registered.
 */
export function triggerBackgroundSync() {
  if (window.swRegistration && 'sync' in window.swRegistration) {
    return window.swRegistration.sync.register('sync-documents')
      .then(() => {
        console.log('[ServiceWorker] Background sync registered');
        return true;
      })
      .catch(error => {
        console.error('[ServiceWorker] Failed to register background sync:', error);
        return false;
      });
  }
  console.log('[ServiceWorker] Background sync not supported or registration not available');
  return Promise.resolve(false);
}

/**
 * Unregister all service workers.
 * @returns {Promise<boolean>} Resolves to true if all service workers were unregistered.
 */
export function unregisterAll() {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.getRegistrations()
      .then(registrations => {
        return Promise.all(
          registrations.map(registration => registration.unregister())
        );
      })
      .then(results => {
        console.log('[ServiceWorker] All service workers unregistered');
        return results.every(result => result === true);
      })
      .catch(error => {
        console.error('[ServiceWorker] Error unregistering service workers:', error);
        return false;
      });
  }
  return Promise.resolve(false);
}

/**
 * Unregister the currently active service worker.
 */
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
      })
      .catch(error => {
        console.error(error.message);
      });
  }
}
